import React from 'react';
import { Button } from 'flowbite-react';
import { CircleAlert } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      // Default fallback UI
      return (
        <div className="rounded-lg border border-grey-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <div className="rounded-full bg-red-100 p-3 text-red-500 mb-4">
              <CircleAlert className="h-6 w-6" aria-hidden />
            </div>
            
            <h3 className="mb-2 text-h5 font-bold text-grey-900">
              {this.props.title || "Something went wrong"}
            </h3>
            
            <p className="mb-6 text-grey-600">
              {this.props.description || "We had trouble loading this component."}
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="mb-4 w-full text-left">
                <summary className="cursor-pointer text-primary font-medium">
                  Error Details
                </summary>
                <div className="mt-2 rounded bg-grey-50 p-4 text-small font-mono text-grey-800 overflow-auto">
                  <p>{this.state.error && this.state.error.toString()}</p>
                  <p className="mt-2 font-bold">Component Stack:</p>
                  <pre className="mt-1 whitespace-pre-wrap">
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}
            
            <div className="flex flex-wrap gap-2 justify-center">
              {this.props.showRefreshButton && (
                <Button
                  color="gray"
                  onClick={() => window.location.reload()}
                  className="min-w-[100px]"
                >
                  Refresh Page
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.defaultProps = {
  title: "Something went wrong",
  description: "We had trouble loading this component.",
  showRefreshButton: true,
  onError: null,
  actionButton: false,
  actionButtonText: "",
  actionButtonColor: "gray",
  actionButtonOnClick: () => {}
};

export default ErrorBoundary; 